import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PremiumLock } from "@/components/Upgrade";
import { Button } from "@/components/ui/button";
import { Stethoscope, Loader2, AlertTriangle, CheckCircle2, Bug, Leaf, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/plant";

const HEALTH_STYLE = {
  Healthy: { color: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300", icon: CheckCircle2 },
  "Needs attention": { color: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300", icon: AlertTriangle },
  Unhealthy: { color: "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300", icon: AlertTriangle },
};

export function AiPlantDoctor({ plant, hasPhoto }) {
  const { user } = useAuth();
  const [result, setResult] = useState(plant?.last_diagnosis || null);
  const [loading, setLoading] = useState(false);

  if (user && !user.is_premium) {
    return (
      <PremiumLock
        title="AI Plant Doctor is a Pro feature"
        description="Snap a photo and let AI spot yellowing, pests and health issues with care tips."
        features={["AI photo health analysis", "AI Identify & Care Assistant", "Unlimited sprouts"]}
      />
    );
  }

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/plants/${plant.id}/diagnose`);
      setResult(data);
      toast.success("Diagnosis complete");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const style = result ? HEALTH_STYLE[result.health] || HEALTH_STYLE["Needs attention"] : null;
  const HealthIcon = style?.icon || Sparkles;

  return (
    <div className="bg-card rounded-2xl border border-border p-5 sm:p-6 shadow-sm" data-testid="ai-plant-doctor">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="font-display text-lg font-semibold flex items-center gap-2">
          <span className="h-8 w-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
            <Stethoscope className="h-5 w-5 text-emerald-600" />
          </span>
          AI Plant Doctor
        </h2>
        <Button onClick={run} disabled={loading || !hasPhoto} className="bg-emerald-700 hover:bg-emerald-800 rounded-xl" data-testid="diagnose-button">
          {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
          {result ? "Re-analyze" : "Analyze photo"}
        </Button>
      </div>

      {!hasPhoto ? (
        <p className="text-sm text-muted-foreground">Add a photo of this sprout to let the AI check its health.</p>
      ) : loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
          <Loader2 className="h-4 w-4 animate-spin" /> Examining leaves, color and posture…
        </div>
      ) : !result ? (
        <p className="text-sm text-muted-foreground">Tap “Analyze photo” for an AI health check — yellowing, pests, and care tips.</p>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${style.color}`} data-testid="diagnosis-health">
              <HealthIcon className="h-4 w-4" /> {result.health}
            </span>
            {result.yellowing && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300">
                <Leaf className="h-3.5 w-3.5" /> Yellowing
              </span>
            )}
            {result.pests && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300">
                <Bug className="h-3.5 w-3.5" /> Pests detected
              </span>
            )}
          </div>

          {result.summary && <p className="text-sm text-foreground/90">{result.summary}</p>}

          {result.issues?.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Issues</p>
              <ul className="space-y-1">
                {result.issues.map((it, i) => (
                  <li key={i} className="text-sm flex gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" /> {it}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.recommendations?.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Recommendations</p>
              <ul className="space-y-1">
                {result.recommendations.map((r, i) => (
                  <li key={i} className="text-sm flex gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> {r}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.diagnosed_at && <p className="text-xs text-muted-foreground">Last analyzed {formatDate(result.diagnosed_at)}</p>}
        </div>
      )}
    </div>
  );
}
